import { useEffect, useState } from "react";
import {
  AlertOctagon,
  ShieldAlert,
  CheckCircle,
  TrendingDown,
  Search,
  FolderOpen,
} from "lucide-react";
import AdminShell, { Panel, StatusPill, Btn } from "@/components/admin/AdminShell";
import KpiCard from "@/components/admin/KpiCard";
import { PrimaryTabs, FilterChips } from "@/components/admin/SubTabs";
import DataTable from "@/components/admin/DataTable";
import AdminPager from "@/components/admin/AdminPager";
import { api } from "@/lib/api";
import { asAdminList, useAdminPager, unwrapAdminList } from "@/lib/useAdminPager";

const ALERT_TABS = [
  { id: "wash_trade", label: "Wash Trade Alerts", icon: AlertOctagon },
  { id: "spoof", label: "Spoofing Alerts", icon: ShieldAlert },
  { id: "cases", label: "Investigation Cases", icon: FolderOpen },
];

const STATUS_FILTERS = [
  { id: "", label: "All" },
  { id: "open", label: "Open" },
  { id: "investigating", label: "Investigating" },
  { id: "closed", label: "Closed" },
  { id: "dismissed", label: "Dismissed" },
];

export default function MarketSurveillance() {
  const [tab, setTab] = useState("wash_trade");
  const [statusFilter, setStatusFilter] = useState("open");
  const [alerts, setAlerts] = useState([]);
  const [cases, setCases] = useState([]);
  const [busy, setBusy] = useState(null);
  const pager = useAdminPager();

  async function loadAlerts() {
    try {
      const params = { skip: pager.skip, limit: pager.limit };
      if (statusFilter) params.status = statusFilter;
      if (tab !== "cases") params.alert_type = tab;
      const { data } = await api.get("/api/admin/safety/surveillance", { params });
      const { items, total } = unwrapAdminList(data, "alerts");
      setAlerts(items);
      pager.setTotal(total);
    } catch (e) {
      console.error(e);
    }
  }

  async function loadCases() {
    try {
      const params = { skip: pager.skip, limit: pager.limit };
      if (statusFilter) params.status = statusFilter;
      const { data } = await api.get("/api/admin/safety/surveillance/cases", { params });
      const { items, total } = unwrapAdminList(data, "cases");
      setCases(items);
      pager.setTotal(total);
    } catch (e) {
      console.error(e);
    }
  }

  useEffect(() => { pager.resetPage(); /* eslint-disable-next-line */ }, [tab, statusFilter]);
  useEffect(() => {
    if (tab === "cases") {
      loadCases();
    } else {
      loadAlerts();
    }
    const interval = setInterval(() => {
      if (tab === "cases") loadCases();
      else loadAlerts();
    }, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, statusFilter, pager.page]);

  async function setAlertStatus(alertId, status) {
    const note =
      status === "investigating"
        ? window.prompt("Investigation note (optional):") || undefined
        : status === "closed"
          ? window.prompt("Closure note (optional):") || undefined
          : undefined;
    setBusy(alertId);
    try {
      await api.post(`/api/admin/safety/surveillance/${alertId}/status`, {
        status,
        note,
      });
      loadAlerts();
      loadCases();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to update alert");
    } finally {
      setBusy(null);
    }
  }

  async function setCaseStatus(caseId, status) {
    const note = window.prompt("Case note (optional):") || undefined;
    setBusy(caseId);
    try {
      await api.post(`/api/admin/safety/surveillance/cases/${caseId}/status`, {
        status,
        note,
      });
      loadCases();
      loadAlerts();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to update case");
    } finally {
      setBusy(null);
    }
  }

  const alertRows = asAdminList(alerts);
  const washTradeAlerts = alertRows.filter((a) => a.alert_type === "wash_trade");
  const spoofingAlerts = alertRows.filter((a) => a.alert_type === "spoof");
  const openWash = washTradeAlerts.filter((a) => a.status === "open").length;
  const openSpoof = spoofingAlerts.filter((a) => a.status === "open").length;
  const investigating = alertRows.filter((a) => a.status === "investigating").length;

  const activeAlerts = tab === "wash_trade" ? washTradeAlerts : tab === "spoof" ? spoofingAlerts : [];

  return (
    <AdminShell title="Surveillance" subtitle="Wash trading, spoofing and investigation cases.">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        <KpiCard label="Open Wash Alerts" value={openWash} icon={AlertOctagon} variant="red" />
        <KpiCard label="Open Spoof Alerts" value={openSpoof} icon={ShieldAlert} variant="yellow" />
        <KpiCard label="Under Investigation" value={investigating} icon={Search} variant="purple" />
        <KpiCard label="Surveillance Worker" value="ONLINE" icon={TrendingDown} variant="blue" />
      </div>

      <PrimaryTabs tabs={ALERT_TABS} value={tab} onChange={setTab} testIdPrefix="surveillance-tab" />

      <div className="mt-4 mb-4">
        <FilterChips
          options={STATUS_FILTERS}
          value={statusFilter}
          onChange={setStatusFilter}
          testIdPrefix="surv-status"
        />
      </div>

      <div className="mt-2">
        {tab === "cases" ? (
          <Panel>
            <div className="p-4 border-b border-line">
              <h3 className="text-[14px] font-semibold">Investigation Cases</h3>
            </div>
            <DataTable
              columns={[
                { key: "case", header: "Case ID", render: (c) => <span className="font-mono text-[11px]">{c.case_id}</span> },
                { key: "pair", header: "Pair", render: (c) => c.pair },
                { key: "type", header: "Type", render: (c) => c.primary_type },
                { key: "alerts", header: "Alerts", align: "center", render: (c) => c.alert_count ?? 0 },
                { key: "status", header: "Status", render: (c) => <StatusPill value={c.status} /> },
                {
                  key: "act",
                  header: "",
                  align: "right",
                  render: (c) =>
                    c.status === "open" || c.status === "investigating" ? (
                      <div className="flex gap-2 justify-end">
                        {c.status === "open" && (
                          <Btn
                            kind="primary"
                            disabled={busy === c.case_id}
                            onClick={() => setCaseStatus(c.case_id, "investigating")}
                            className="h-7 text-[11px] px-2"
                          >
                            Investigate
                          </Btn>
                        )}
                        <Btn
                          kind="success"
                          disabled={busy === c.case_id}
                          onClick={() => setCaseStatus(c.case_id, "closed")}
                          className="h-7 text-[11px] px-2"
                        >
                          Close
                        </Btn>
                        <Btn
                          kind="ghost"
                          disabled={busy === c.case_id}
                          onClick={() => setCaseStatus(c.case_id, "dismissed")}
                          className="h-7 text-[11px] px-2"
                        >
                          Dismiss
                        </Btn>
                      </div>
                    ) : (
                      <span className="text-[11px] text-muted-foreground">Closed</span>
                    ),
                },
              ]}
              rows={cases}
              rowKey={(c) => c.case_id}
              empty="No investigation cases."
            />
            <AdminPager page={pager.page} total={pager.total} pageSize={pager.pageSize} onPage={pager.setPage} testId="surv-cases-pager" />
          </Panel>
        ) : (
          <Panel>
            <div className="p-4 border-b border-line">
              <h3 className="text-[14px] font-semibold">
                {tab === "wash_trade"
                  ? "Wash Trading Detection Feed"
                  : "Spoofing / Order Book Manipulation Feed"}
              </h3>
            </div>
            <DataTable
              columns={[
                { key: "pair", header: "Market Pair", render: (a) => <span className="font-semibold">{a.pair}</span> },
                {
                  key: "case",
                  header: "Case",
                  render: (a) => (
                    <span className="font-mono text-[10px] text-muted-foreground">{a.case_id || "—"}</span>
                  ),
                },
                {
                  key: "users",
                  header: "Involved User(s)",
                  render: (a) => (
                    <div>
                      {a.user_names?.map((name, i) => (
                        <div key={i} className="text-[12px]">
                          <span className="font-semibold">{name}</span>
                          <span className="text-muted-foreground text-[11px] block">{a.user_emails?.[i]}</span>
                        </div>
                      ))}
                    </div>
                  ),
                },
                {
                  key: "details",
                  header: "Findings",
                  render: (a) => (
                    <span className="text-[12px] text-muted-foreground block max-w-sm whitespace-pre-wrap">
                      {a.details}
                    </span>
                  ),
                },
                {
                  key: "confidence",
                  header: "Confidence",
                  align: "center",
                  render: (a) => (
                    <span
                      className={`font-semibold font-mono ${
                        a.confidence >= 80 ? "text-down" : "text-warning"
                      }`}
                    >
                      {a.confidence}%
                    </span>
                  ),
                },
                {
                  key: "time",
                  header: "Detected",
                  render: (a) => (
                    <span className="text-[12px] text-muted-foreground">
                      {new Date(a.created_at).toLocaleString()}
                    </span>
                  ),
                },
                { key: "status", header: "Status", render: (a) => <StatusPill value={a.status} /> },
                {
                  key: "actions",
                  header: "Actions",
                  align: "right",
                  render: (a) =>
                    a.status === "open" || a.status === "investigating" ? (
                      <div className="flex gap-1 justify-end flex-wrap">
                        {a.status === "open" && (
                          <Btn
                            kind="primary"
                            disabled={busy === a.alert_id}
                            onClick={() => setAlertStatus(a.alert_id, "investigating")}
                            className="h-7 text-[11px] px-2"
                          >
                            Investigate
                          </Btn>
                        )}
                        <Btn
                          kind="success"
                          disabled={busy === a.alert_id}
                          onClick={() => setAlertStatus(a.alert_id, "closed")}
                          className="h-7 text-[11px] px-2"
                        >
                          <CheckCircle size={11} /> Close
                        </Btn>
                        <Btn
                          kind="ghost"
                          disabled={busy === a.alert_id}
                          onClick={() => setAlertStatus(a.alert_id, "dismissed")}
                          className="h-7 text-[11px] px-2"
                        >
                          Dismiss
                        </Btn>
                      </div>
                    ) : (
                      <span className="text-[11px] text-muted-foreground italic">Resolved</span>
                    ),
                },
              ]}
              rows={activeAlerts}
              rowKey={(a) => a.alert_id}
              empty={`No ${tab === "wash_trade" ? "wash trading" : "spoofing"} alerts for this filter.`}
            />
            <AdminPager page={pager.page} total={pager.total} pageSize={pager.pageSize} onPage={pager.setPage} testId="surv-alerts-pager" />
          </Panel>
        )}
      </div>
    </AdminShell>
  );
}
